import { AppDispatch } from "app/store";
import OmronFooter from "components/OmronFooter";
import OmronHeader from "components/OmronHeader";
import OmronSidebar from "components/OmronSidebar";
import { logoutAsync, selectUserState, userAsync } from "features/auth";
import {
  selectNewPassword,
  selectCurrentPassword,
  selectNewPasswordCheck,
  selectPasswordValidationErrors,
  changePasswordAsync,
  changeCurrentPassword,
  changeNewPasswordCheck,
  changeNewPassword,
} from "features/profile";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled, { css } from "styled-components";

const ChangePasswordPage: React.FC = (): JSX.Element => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useSelector(selectUserState);
  const errors = useSelector(selectPasswordValidationErrors);
  const newPassword = useSelector(selectNewPassword);
  const currentPassword = useSelector(selectCurrentPassword);
  const newPasswordConfirm = useSelector(selectNewPasswordCheck);

  const [error, SetError] = useState({
    hasErrorEmptyPassword: false,
    hasErrorIncorrectPassword: false,
    hasErrorNotMatchPassword: false,
  });

  const onSubmit = useCallback(async () => {
    SetError(errors);
    if (
      errors.hasErrorEmptyPassword ||
      errors.hasErrorIncorrectPassword ||
      errors.hasErrorNotMatchPassword
    ) {
      return;
    }

    const { meta } = await dispatch(changePasswordAsync());

    if (meta.requestStatus === "fulfilled") {
      navigate("/profile", { state: { from: "changePassword" } });
    }
  }, [errors, dispatch, navigate]);

  const logout = useCallback(async () => {
    const { meta } = await dispatch(logoutAsync());
    if (meta.requestStatus === "rejected") {
      return false;
    }
    window.location.pathname = "/";
    return true;
  }, [dispatch]);

  // 初期読込
  useEffect(() => {
    dispatch(userAsync());
  }, [dispatch]);
  return (
    <MainWrapper>
      <OmronHeader
        link={{ path: "/", label: t("common.label.breadcrumbsSignin") }}
        label={[
          t("common.label.breadcrumbsProfile"),
          t("common.label.breadcrumbsChangePassword"),
        ]}
        user={user}
      />
      <OmronSidebar profile onLogout={logout} />
      <Main>
        <InnerDiv>
          <div>
            <Title>{t("profilePage.label.changePassword")}</Title>
            <Text />
          </div>
          <section>
            <form>
              <Dl>
                <Dt>
                  {t("profilePage.label.currentPassword")}
                  <Required>{t("profilePage.label.required")}</Required>
                </Dt>
                <Dd>
                  <PasswordInput
                    type="password"
                    size={40}
                    value={currentPassword}
                    onChange={(e) => {
                      dispatch(
                        changeCurrentPassword({
                          currentPassword: e.target.value,
                        })
                      );
                    }}
                  />
                </Dd>
                <PasswordDt>
                  {t("profilePage.label.newPassword")}{" "}
                  <Required>{t("profilePage.label.required")}</Required>
                  <Span>
                    {t("profilePage.label.passwordAllowdCharactoers")}
                    <br />
                    {t("profilePage.label.passwordContainsLowerCase")}
                    <br />
                    {t("profilePage.label.passwordContainsUpperCase")}
                    <br />
                    {t("profilePage.label.passwordContainsNumberAndSymbol")}
                  </Span>
                </PasswordDt>
                <PasswordDd>
                  <PasswordInput
                    type="password"
                    size={40}
                    value={newPassword}
                    isError={
                      error.hasErrorEmptyPassword ||
                      error.hasErrorIncorrectPassword
                    }
                    onChange={(e) => {
                      dispatch(
                        changeNewPassword({ newPassword: e.target.value })
                      );
                    }}
                  />
                  <br />
                  {error.hasErrorEmptyPassword && (
                    <ErrorComment>
                      {t("profilePage.message.requiredEmptyError")}
                    </ErrorComment>
                  )}
                  {error.hasErrorIncorrectPassword && (
                    <ErrorComment>
                      {t("profilePage.message.notAllowedPasswordRuleError")}
                    </ErrorComment>
                  )}
                  <PasswordConfirmInput
                    type="password"
                    size={40}
                    value={newPasswordConfirm}
                    isError={error.hasErrorNotMatchPassword}
                    onChange={(e) => {
                      dispatch(
                        changeNewPasswordCheck({
                          passwordCheck: e.target.value,
                        })
                      );
                    }}
                    placeholder={t("profilePage.label.confirm")}
                  />
                  {error.hasErrorNotMatchPassword && (
                    <ErrorComment>
                      {t("profilePage.message.notMatchPasswordError")}
                    </ErrorComment>
                  )}
                </PasswordDd>
                <DdFull>
                  <Link href="/profile">
                    {t("profilePage.label.backButton")}
                  </Link>
                  <Button
                    type="button"
                    value={t("profilePage.label.changgePasswordButton")}
                    onClick={onSubmit}
                  />
                </DdFull>
              </Dl>
            </form>
          </section>
        </InnerDiv>
      </Main>
      <OmronFooter />
    </MainWrapper>
  );
};
export default ChangePasswordPage;

const MainWrapper = styled.div`
  display: grid;
  grid-template-rows: 76px 40px 1fr auto;
  grid-template-columns: 20rem 1fr;
  grid-template-areas:
    "header header"
    "breadc breadc"
    "side main"
    "side footer";
  min-height: 100vh;
`;
const Main = styled.main`
  grid-area: main;
  padding: 3.2rem 50px;
`;

const InnerDiv = styled.div`
  width: 1000px;
`;

const Title = styled.h1`
  margin-bottom: 3rem;
`;

const Text = styled.p`
  margin-bottom: 3rem;
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 0;
  font-weight: normal;
`;

const Dl = styled.dl`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 3rem;
`;
const Dt = styled.dt`
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 0;
  font-weight: normal;
  background: #fafafa;
  width: 26%;
  padding: 1.3rem 2%;
  :nth-of-type(2n) {
    background: #f0f0f0;
  }
`;
const PasswordDt = styled(Dt)`
  margin-bottom: 2rem;
`;

const Dd = styled.dd`
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 0;
  font-weight: normal;
  background: #fafafa;
  width: 70%;
  padding: 1rem 0;
  :nth-of-type(2n) {
    background: #f0f0f0;
  }
`;

const PasswordDd = styled(Dd)`
  margin-bottom: 2rem;
`;

const DdFull = styled(Dd)`
  text-align: center;
  width: 100%;
  background: none;
`;

const PasswordInput = styled.input<{ isError?: boolean }>`
  padding: 0.5rem 0.8rem;
  border: 1px #999999 solid;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
  ${({ isError }) =>
    isError &&
    css`
      background: rgba(229, 0, 0, 0.08);
      :focus {
        background: rgba(229, 0, 0, 0.08);
      }
    `}
`;

const PasswordConfirmInput = styled(PasswordInput)`
  margin-top: 1rem;
`;

const Link = styled.a`
  display: inline-block;
  margin: 0 3rem;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
  color: #005eb8;
  text-decoration: underline;
  cursor: pointer;
`;

const Button = styled.input`
  min-width: 15rem;
  padding: 1.23rem 2rem;
  transition: all 0.3s ease-out;
  width: 10rem;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
  cursor: pointer;
  opacity: 1;
  color: #ffffff;
  background: #005eb8;
  border: 1px #005eb8 solid;
  pointer-events: auto;
  :hover {
    background: rgba(0, 94, 184, 0.7);
  }
`;

const Span = styled.span`
  display: block;
  color: #999999;
  font-size: 11px;
  line-height: 1.6;
  letter-spacing: 0;
  font-weight: normal;
`;

const ErrorComment = styled.span`
  margin-top: 0.2rem;
  display: block;
  color: #e50000;
  font-size: 11px;
  line-height: 1.6;
  letter-spacing: 0;
  font-weight: normal;
`;

const Required = styled.p`
  display: inline-block;
  color: #e50000;
  float: right;
  padding-top: 0.3rem;
  font-size: 11px;
  line-height: 1.6;
  letter-spacing: 0;
  font-weight: normal;
`;
